import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { api, adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const ProjectsManager = () => {
  const [projects, setProjects] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await api.getProjects();
      setProjects(response.data);
    } catch (error) {
      toast({ title: 'Error loading projects', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await adminApi.deleteProject(id);
      toast({ title: 'Project deleted' });
      loadProjects();
    } catch (error) {
      toast({ title: 'Error deleting project', variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    try {
      if (isAdding) {
        await adminApi.addProject(editData);
        toast({ title: 'Project added!' });
      } else {
        await adminApi.updateProject(editingId, editData);
        toast({ title: 'Project updated!' });
      }
      setEditingId(null);
      setEditData(null);
      setIsAdding(false);
      loadProjects();
    } catch (error) {
      toast({ title: 'Error saving project', variant: 'destructive' });
    }
  };

  const startEdit = (project) => {
    setEditingId(project.id);
    setEditData({ ...project });
  };

  const startAdd = () => {
    setIsAdding(true);
    setEditData({
      title: '',
      description: { fr: '', en: '' },
      category: 'Web App',
      tech: [],
      image: '',
      year: new Date().getFullYear().toString(),
      link: '#',
      featured: false
    });
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Projects</h1>
          <p className="text-gray-400">Manage portfolio projects</p>
        </div>
        <motion.button
          onClick={startAdd}
          whileHover={{ scale: 1.05 }}
          className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Project
        </motion.button>
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingId) && (
        <div className="mb-8 bg-gradient-to-br from-[#1a1a1b] to-[#0f0f10] rounded-xl border border-cyan-500/20 p-6">
          <h3 className="text-xl font-bold text-cyan-400 mb-4">{isAdding ? 'Add New Project' : 'Edit Project'}</h3>
          <div className="space-y-4">
            <input
              type="text"
              value={editData.title}
              onChange={(e) => setEditData({...editData, title: e.target.value})}
              placeholder="Project Title"
              className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
            />
            <div className="grid grid-cols-2 gap-4">
              <textarea
                value={editData.description.fr}
                onChange={(e) => setEditData({...editData, description: {...editData.description, fr: e.target.value}})}
                placeholder="Description (FR)"
                rows={3}
                className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
              />
              <textarea
                value={editData.description.en}
                onChange={(e) => setEditData({...editData, description: {...editData.description, en: e.target.value}})}
                placeholder="Description (EN)"
                rows={3}
                className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
              />
            </div>
            <input
              type="url"
              value={editData.image}
              onChange={(e) => setEditData({...editData, image: e.target.value})}
              placeholder="Image URL"
              className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
            />
            <div className="flex gap-4">
              <button onClick={handleSave} className="px-6 py-2 bg-cyan-500 text-white rounded-lg flex items-center gap-2">
                <Save className="w-4 h-4" />
                Save
              </button>
              <button onClick={() => { setEditingId(null); setIsAdding(false); }} className="px-6 py-2 bg-gray-700 text-white rounded-lg flex items-center gap-2">
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Projects List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {projects.map(project => (
          <div key={project.id} className="bg-gradient-to-br from-[#1a1a1b] to-[#0f0f10] rounded-xl border border-cyan-500/10 p-6">
            <img src={project.image} alt={project.title} className="w-full h-40 object-cover rounded-lg mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
            <p className="text-gray-400 text-sm mb-4">{project.category} • {project.year}</p>
            <div className="flex gap-2">
              <button onClick={() => startEdit(project)} className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg flex items-center gap-2">
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button onClick={() => handleDelete(project.id)} className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg flex items-center gap-2">
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectsManager;
